import { useEffect, useState } from "react";
import { Button, DatePicker, Input, Select, Switch } from "antd";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { LeftOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import dayjs from "dayjs";
import { Hero } from "../components/Hero";
import { Loader } from "../common/Loader";
import {
  getAllBoards,
  getAllColumnsOfBoard,
  getCustomReportsAdmin,
  saveCustomReport,
} from "../apiservice/ApiService";

export const CustomReportSetting = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [allBoardId, setAllBoardId] = useState([]);
  const [columnOptions, setColumnOptions] = useState([]); // [{label, value}] all columns
  const [boardColumns, setBoardColumns] = useState([]); // full meta [{id,title,type,labels}]

  const [selectedProfileId, setSelectedProfileId] = useState(undefined);
  const [selectedBoardId, setSelectedBoardId] = useState(undefined);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [filterColumn, setFilterColumn] = useState(undefined);
  const [filterValue, setFilterValue] = useState("");
  // Monday-style filters
  const [statusFilters, setStatusFilters] = useState([]); // [{column_id, labels:[idx]}]
  const [dateFilters, setDateFilters] = useState([]); // [{column_id, from, to}]
  const [enabled, setEnabled] = useState(false);
  const [reportTitle, setReportTitle] = useState("Custom Report");

  const filterOption = (input, option) =>
    option.label.toLowerCase().includes(input.toLowerCase()) ||
    option.value.toString().toLowerCase().includes(input.toLowerCase());

  const parseJSON = (val, fallback) => {
    if (!val) return fallback;
    try {
      return typeof val === "string" ? JSON.parse(val) : val;
    } catch (e) {
      return fallback;
    }
  };

  // Status-type columns (status/color) for the status filter dropdown
  const statusColumnOptions = boardColumns
    .filter((c) => c.type === "status" || c.type === "color")
    .map((c) => ({ label: c.title, value: c.id }));
  // Date-type columns for the date filter dropdown
  const dateColumnOptions = boardColumns
    .filter((c) => c.type === "date")
    .map((c) => ({ label: c.title, value: c.id }));

  // Label options ({label, value:index}) for a given status column
  const getLabelOptions = (columnId) => {
    const col = boardColumns.find((c) => c.id === columnId);
    if (!col || !col.labels) return [];
    return Object.entries(col.labels)
      .filter(([, lbl]) => lbl && lbl.toString().trim() !== "")
      .map(([idx, lbl]) => ({ label: lbl, value: Number(idx) }));
  };

  const loadColumnsForBoard = async (boardId) => {
    const response = await getAllColumnsOfBoard(boardId);
    if (response.success) {
      const cols = response.data.response;
      setColumnOptions(cols.map((c) => ({ label: c.title, value: c.id })));
      setBoardColumns(
        cols.map((c) => {
          const settings = parseJSON(c.settings_str, {});
          return {
            id: c.id,
            title: c.title,
            type: c.type,
            labels: settings.labels || {},
          };
        })
      );
    } else {
      setColumnOptions([]);
      setBoardColumns([]);
    }
  };

  const init = async () => {
    setLoading(true);
    try {
      const [reportsRes, boardsRes] = await Promise.all([
        getCustomReportsAdmin(),
        getAllBoards(),
      ]);
      if (reportsRes.success) setProfiles(reportsRes.data.response);
      if (boardsRes.success) {
        setAllBoardId(
          boardsRes.data.response.boards.map((item) => ({
            label: item.name,
            value: item.id,
          }))
        );
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    init();
  }, []);

  const handleSelectProfile = async (profileId) => {
    setSelectedProfileId(profileId);
    const profile = profiles.find((p) => p.profile_id === profileId);
    const savedColumns = parseJSON(profile?.columns, []);
    setEnabled(Boolean(profile?.enabled));
    setReportTitle(profile?.report_title || "Custom Report");

    const filterObj = parseJSON(profile?.filter, null);
    setFilterColumn(filterObj?.key || undefined);
    setFilterValue(filterObj?.value || "");

    const adv = parseJSON(profile?.advanced_filters, null);
    setStatusFilters(Array.isArray(adv?.status) ? adv.status : []);
    setDateFilters(Array.isArray(adv?.dates) ? adv.dates : []);

    if (profile?.board_id) {
      setSelectedBoardId(profile.board_id);
      setLoading(true);
      try {
        await loadColumnsForBoard(profile.board_id);
        setSelectedColumns(
          Array.isArray(savedColumns) ? savedColumns.map((c) => c.id) : []
        );
      } finally {
        setLoading(false);
      }
    } else {
      setSelectedBoardId(undefined);
      setColumnOptions([]);
      setBoardColumns([]);
      setSelectedColumns([]);
    }
  };

  const handleSelectBoard = async (boardId) => {
    setSelectedBoardId(boardId);
    setSelectedColumns([]);
    setFilterColumn(undefined);
    setFilterValue("");
    setStatusFilters([]);
    setDateFilters([]);
    if (boardId === undefined) {
      setColumnOptions([]);
      setBoardColumns([]);
      return;
    }
    setLoading(true);
    try {
      await loadColumnsForBoard(boardId);
    } finally {
      setLoading(false);
    }
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;
    const newData = [...selectedColumns];
    const [removed] = newData.splice(source.index, 1);
    newData.splice(destination.index, 0, removed);
    setSelectedColumns(newData);
  };

  const getColumnLabel = (id) => {
    const found = columnOptions.find((c) => c.value === id);
    return found ? found.label : id;
  };

  // ----- status filter helpers -----
  const addStatusFilter = () =>
    setStatusFilters([...statusFilters, { column_id: undefined, labels: [] }]);
  const updateStatusFilter = (idx, field, val) => {
    const next = [...statusFilters];
    next[idx] = { ...next[idx], [field]: val };
    if (field === "column_id") next[idx].labels = []; // reset labels when column changes
    setStatusFilters(next);
  };
  const removeStatusFilter = (idx) =>
    setStatusFilters(statusFilters.filter((_, i) => i !== idx));

  // ----- date filter helpers -----
  const addDateFilter = () =>
    setDateFilters([...dateFilters, { column_id: undefined, from: "", to: "" }]);
  const updateDateFilter = (idx, field, val) => {
    const next = [...dateFilters];
    next[idx] = { ...next[idx], [field]: val };
    setDateFilters(next);
  };
  const removeDateFilter = (idx) =>
    setDateFilters(dateFilters.filter((_, i) => i !== idx));

  const handleSave = async () => {
    if (selectedProfileId === undefined)
      return toast.error("Please select a client/profile.");
    if (!selectedBoardId) return toast.error("Please assign a board.");
    if (selectedColumns.length === 0)
      return toast.error("Please select at least one column.");

    const advanced_filters = {
      status: statusFilters.filter(
        (f) => f.column_id && Array.isArray(f.labels) && f.labels.length > 0
      ),
      dates: dateFilters.filter((f) => f.column_id && (f.from || f.to)),
    };
    const hasAdvanced =
      advanced_filters.status.length > 0 || advanced_filters.dates.length > 0;

    setLoading(true);
    const payload = {
      profile_id: selectedProfileId.toString(),
      board_id: selectedBoardId,
      columns: selectedColumns.map((id) => ({ id, title: getColumnLabel(id) })),
      filter:
        filterColumn && filterValue.trim() !== ""
          ? { key: filterColumn, value: filterValue.trim() }
          : null,
      advanced_filters: hasAdvanced ? advanced_filters : null,
      enabled: enabled ? 1 : 0,
      report_title: reportTitle || "Custom Report",
    };
    try {
      const response = await saveCustomReport(payload);
      if (response.success) {
        toast.success("Custom Report Settings Saved!");
        await init();
      } else {
        toast.error(response.message);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const labelStyle = { marginRight: "20px", display: "inline-block", minWidth: "180px" };
  const sectionStyle = {
    marginTop: "24px",
    paddingTop: "16px",
    borderTop: "1px solid #eee",
  };

  return (
    <div>
      <div style={{ marginTop: "48px", marginBottom: "16px" }}>
        <Hero
          heading={"Custom Report Settings"}
          subheading="Configure a tabular report and choose which client it renders for"
          forHome={false}
        />
      </div>
      {loading && <Loader />}
      <div style={{ display: "flex", alignItems: "center", paddingBottom: "10px" }}>
        <Button icon={<LeftOutlined />} onClick={() => navigate(-1)}></Button>
      </div>

      <div style={{ textAlign: "left", maxWidth: "780px", margin: "0 auto" }}>
        <div style={{ marginTop: "20px" }}>
          <span style={labelStyle}>Select Client (Profile) : </span>
          <Select
            showSearch
            placeholder="Select a client/profile"
            style={{ width: "55%" }}
            value={selectedProfileId}
            onChange={handleSelectProfile}
            options={profiles.map((p) => ({ label: p.title, value: p.profile_id }))}
            filterOption={filterOption}
          />
        </div>

        {selectedProfileId !== undefined && (
          <>
            <div style={{ marginTop: "20px" }}>
              <span style={labelStyle}>Enabled : </span>
              <Switch checked={enabled} onChange={setEnabled} />
            </div>

            <div style={{ marginTop: "20px" }}>
              <span style={labelStyle}>Report Title : </span>
              <Input
                style={{ width: "55%" }}
                value={reportTitle}
                onChange={(e) => setReportTitle(e.target.value)}
              />
            </div>

            <div style={{ marginTop: "20px" }}>
              <span style={labelStyle}>Assign Board : </span>
              <Select
                showSearch
                allowClear
                placeholder="Please assign a board"
                style={{ width: "55%" }}
                value={selectedBoardId || undefined}
                onChange={handleSelectBoard}
                options={allBoardId}
                filterOption={filterOption}
              />
            </div>

            <div style={{ marginTop: "20px" }}>
              <span style={labelStyle}>Select Table Columns : </span>
              <Select
                mode="multiple"
                showSearch
                allowClear
                style={{ width: "55%" }}
                placeholder="Please select table columns"
                onChange={setSelectedColumns}
                options={columnOptions}
                value={selectedColumns}
                filterOption={filterOption}
              />
            </div>

            {/* Column ordering */}
            <div style={{ marginTop: "16px" }}>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="droppable-columns">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {selectedColumns.map((item, index) => (
                        <Draggable key={item} draggableId={`${item}`} index={index}>
                          {(prov) => (
                            <div
                              ref={prov.innerRef}
                              {...prov.draggableProps}
                              {...prov.dragHandleProps}
                              style={{
                                ...prov.draggableProps.style,
                                marginTop: "8px",
                                border: "1px solid #ddd",
                                padding: "8px 10px",
                                backgroundColor: "white",
                                borderRadius: "4px",
                              }}
                            >
                              {getColumnLabel(item)}
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>

            {/* Client scope (text contains) */}
            <div style={sectionStyle}>
              <b>Client Scope Filter</b>
              <div style={{ marginTop: "12px" }}>
                <span style={labelStyle}>Filter Column : </span>
                <Select
                  showSearch
                  allowClear
                  style={{ width: "55%" }}
                  placeholder="Optional — column to scope rows by"
                  onChange={(v) => setFilterColumn(v)}
                  options={columnOptions}
                  value={filterColumn || undefined}
                  filterOption={filterOption}
                />
              </div>
              {filterColumn && (
                <div style={{ marginTop: "12px" }}>
                  <span style={labelStyle}>Filter Value : </span>
                  <Input
                    style={{ width: "55%" }}
                    placeholder="e.g. MDLBEAST (Remote) — comma-separate for multiple"
                    value={filterValue}
                    onChange={(e) => setFilterValue(e.target.value)}
                  />
                </div>
              )}
            </div>

            {/* Status filters */}
            <div style={sectionStyle}>
              <b>Status Filters</b>
              <span style={{ color: "#888", marginLeft: "8px" }}>
                (show rows matching the selected labels)
              </span>
              {statusFilters.map((f, idx) => (
                <div
                  key={idx}
                  style={{ display: "flex", gap: "10px", marginTop: "12px", alignItems: "center" }}
                >
                  <Select
                    showSearch
                    placeholder="Status column"
                    style={{ width: "240px" }}
                    value={f.column_id}
                    onChange={(v) => updateStatusFilter(idx, "column_id", v)}
                    options={statusColumnOptions}
                    filterOption={filterOption}
                  />
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="Select label(s)"
                    style={{ flex: 1, minWidth: "240px" }}
                    value={f.labels}
                    onChange={(v) => updateStatusFilter(idx, "labels", v)}
                    options={getLabelOptions(f.column_id)}
                    disabled={!f.column_id}
                  />
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removeStatusFilter(idx)}
                  />
                </div>
              ))}
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                style={{ marginTop: "12px" }}
                onClick={addStatusFilter}
              >
                Add status filter
              </Button>
            </div>

            {/* Date filters */}
            <div style={sectionStyle}>
              <b>Date Filters</b>
              <span style={{ color: "#888", marginLeft: "8px" }}>
                (from / until on a date column — leave one side blank for open-ended)
              </span>
              {dateFilters.map((f, idx) => (
                <div
                  key={idx}
                  style={{ display: "flex", gap: "10px", marginTop: "12px", alignItems: "center" }}
                >
                  <Select
                    showSearch
                    placeholder="Date column"
                    style={{ width: "240px" }}
                    value={f.column_id}
                    onChange={(v) => updateDateFilter(idx, "column_id", v)}
                    options={dateColumnOptions}
                    filterOption={filterOption}
                  />
                  <DatePicker
                    placeholder="From"
                    value={f.from ? dayjs(f.from) : null}
                    onChange={(d, ds) => updateDateFilter(idx, "from", ds)}
                  />
                  <DatePicker
                    placeholder="Until"
                    value={f.to ? dayjs(f.to) : null}
                    onChange={(d, ds) => updateDateFilter(idx, "to", ds)}
                  />
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removeDateFilter(idx)}
                  />
                </div>
              ))}
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                style={{ marginTop: "12px" }}
                onClick={addDateFilter}
              >
                Add date filter
              </Button>
            </div>

            <div style={{ marginTop: "28px", marginBottom: "40px" }}>
              <Button type="primary" size="large" onClick={handleSave}>
                Save
              </Button>
            </div>
          </>
        )}
      </div>

      <ToastContainer position="bottom-right" />
    </div>
  );
};
